import { Center, Spinner, useColorMode, useToast } from '@chakra-ui/react';
import { t } from 'i18next';
import { isEmpty, isUndefined } from 'lodash';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import { getArtistId } from '@pocapan/shared/utils/etcUtils';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import useArtistQuery from '../../../components/api/queries/useArtistQuery';
import usePhotoCardEventsQuery from '../../../components/api/queries/usePhotoCardEventsQuery';
import upsertCollectedPhotoCards from '../../../components/api/mutations/upsertCollectedPhotoCards';
import LikedMembersList from '../../../components/lists/LikedMembersList';
import ScrollRestoration from '@pocapan/shared/utils/ScrollRestoration';
import EmptyPhotoCardEventSkeletonV2 from '../../../components/skeletons/EmptyPhotoCardEventSkeletonV2';
import EmptyCase from '../../../components/errors/EmptyCase';
import NewPhotoCardsByEventEditor from '../../../components/virtualized/PhotoCardsByEvent/NewPhotoCardsByEventEditor';
import { ReportPhotoCardBox } from '../../../components/report/ReportPhotoCardBox';
import CommonDialog from '../../../components/alerts/CommonDialog';
import PhotoCardFilterBottomDrawer from '../../../components/drawer/PhotoCardFilterBottomDrawer';
import EditCollectedPhotoCardsHeader from './components/EditCollectedPhotoCardsHeader';
import SwitchColorModeBar from './components/SwitchColorModeBar';
import CollectedPhotoCardActionsBar from './components/CollectedPhotoCardActionsBar';
import {
  getMemberId,
  getOriginalCollectedCount,
  removeFromSelectedData,
  updatePhotoCardWithPrevData
} from './utils/PhotoCardUtils';
import { useSuccessPhotoCardToast } from './hooks/useSuccessPhotoCardToast';
import { sendPhotoCardUpdateMessage } from './utils/PhotoCardApiUtils';
import { useProcessedPhotoCardEvents } from './hooks/useProcessedPhotoCardEvents';
import BaseToastMessageBox from '../../../components/toast/BaseToastMessageBox';

export default function EditCollectedPhotoCardsPage() {
  const { colorMode } = useColorMode();
  // #region states & variables
  const [isOpenExitDialog, setIsOpenExitDialog] = useState(false);
  // 바텀시트 보이는지
  const [currentMember, setCurrentMember] = useState();

  // Initialize state for paginated data and updated data
  const [updatedCollectedData, setUpdatedCollectedData] = useState({});
  const [selectedCollectedData, setSelectedCollectedData] = useState({});
  const [isColorModeOnEditable, setIsColorModeOnEditable] = useState(false);
  const [isShowFilterBottomDrawer, setIsShowFilterBottomDrawer] = useState(false);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [selectedPhotoCardCategories, setSelectedPhotoCardCategories] = useState([]);

  const artistId = getArtistId();
  const queryClient = useQueryClient();
  const handleHistoryBack = useHandleHistoryBack();
  const successPhotoCardToast = useSuccessPhotoCardToast();
  const toast = useToast();
  // #endregion

  // #region API
  const artist = useArtistQuery(artistId);
  const { data, isFetching, fetchNextPage, hasNextPage } = usePhotoCardEventsQuery({
    artistId: artistId,
    memberId: currentMember?.memberId,
    eventIds: selectedEventIds,
    photoCardCategories: selectedPhotoCardCategories
  });
  // Function to handle the received paginated data
  const { paginatedEvents } = useProcessedPhotoCardEvents({
    data,
    updatedCollectedData,
    selectedCollectedData
  });
  // #endregion

  ScrollRestoration(window.location.pathname);

  // #region Function to handle updated data set.
  const handleUpdatedCollectedPhotoCard = (photoCard) => {
    setUpdatedCollectedData((prevData) => {
      const memberId = getMemberId(photoCard, currentMember);
      return updatePhotoCardWithPrevData(prevData, photoCard, memberId);
    });
  };

  const handleSelectedCollectedPhotoCard = (photoCard) => {
    setSelectedCollectedData((prevData) => {
      const memberId = getMemberId(photoCard, currentMember);

      const originalCount = getOriginalCollectedCount(data, photoCard);

      if (photoCard.collectedCount === originalCount) {
        return removeFromSelectedData(prevData, memberId, photoCard.photoCardId);
      }

      return updatePhotoCardWithPrevData(prevData, photoCard, memberId);
    });
  };
  // #endregion

  // #region events
  const loadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const onChangedMember = (member) => {
    setCurrentMember(member);
  };

  const onHistoryBackClick = () => {
    const upsertRequest = selectedCollectedData[currentMember?.memberId];
    if (isEmpty(upsertRequest)) {
      return IgnoreAndExit();
    }
    return setIsOpenExitDialog(true);
  };

  const IgnoreAndExit = () => {
    setSelectedCollectedData({});
    setIsOpenExitDialog(false);
    return handleHistoryBack();
  };

  const onClickPhotoCard = (_event, photoCard) => {
    const memberId = getMemberId(photoCard, currentMember);
    const isAlreadySelected =
      selectedCollectedData[memberId]?.[photoCard.photoCardId] !== undefined;

    if (photoCard.collectedCount > 0) {
      // 보유중인 카드: 이미 선택되어 있으면 선택 해제, 아니면 현재 수량 유지하면서 선택
      if (isAlreadySelected) {
        setSelectedCollectedData((prevData) => {
          return removeFromSelectedData(prevData, memberId, photoCard.photoCardId);
        });
      } else {
        // 선택되지 않은 카드 → 선택 (원래 수량 유지)
        setSelectedCollectedData((prevData) => {
          // 원본 수량을 가져와서 사용
          const originalCount = getOriginalCollectedCount(data, photoCard);

          return updatePhotoCardWithPrevData(
            prevData,
            { ...photoCard, collectedCount: originalCount },
            memberId
          );
        });
      }
    } else {
      handleSelectedCollectedPhotoCard({ ...photoCard, collectedCount: 1 });
    }
  };

  const onColorModeSwitchChanged = () => {
    setIsColorModeOnEditable(!isColorModeOnEditable);
  };

  const onConfirmPhotoCardFilter = (filteredObj) => {
    setIsShowFilterBottomDrawer(false);

    if (!isEmpty(filteredObj)) {
      setSelectedEventIds(filteredObj.eventIds);
      setSelectedPhotoCardCategories(filteredObj.photoCardCategories);

      queryClient.invalidateQueries(['photoCardEvents', artistId]);
      queryClient.invalidateQueries(['collectedPhotoCardIds']);
    }
  };

  const isCheckedPhotoCardFilter = () => {
    return !isEmpty(selectedPhotoCardCategories) || !isEmpty(selectedEventIds);
  };

  const handleUpsertCollectedPhotoCards = async (upsertRequest) => {
    try {
      const response = await upsertCollectedPhotoCards(artistId, upsertRequest);
      if (!response.collectedPhotoCards) {
        throw new Error('API 응답이 올바르지 않습니다');
      }

      response.collectedPhotoCards.forEach((photoCard) => {
        handleUpdatedCollectedPhotoCard(photoCard);
      });

      // 수신 측에서 낙관적 업데이트가 가능하도록 id와 수량을 함께 전송
      const payload = response.collectedPhotoCards.map((photoCard) => ({
        photoCardId: photoCard.photoCardId,
        collectedCount: photoCard.collectedCount
      }));
      sendPhotoCardUpdateMessage(payload);

      setSelectedCollectedData({});
      successPhotoCardToast();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: upsert failure logging
      console.error(error);
      toast({
        position: 'bottom',
        duration: 1500,
        render: () => (
          <BaseToastMessageBox message={t('photoCardPage.errorToastMessage')} showOnBottom={true} />
        )
      });
    }
  };

  const onCompletedEditable = async () => {
    const upsertRequest = selectedCollectedData[currentMember?.memberId];
    if (isEmpty(upsertRequest)) {
      return;
    }
    await handleUpsertCollectedPhotoCards(upsertRequest);
  };

  const onRemoveCollectedCards = async () => {
    const upsertRequest = selectedCollectedData[currentMember?.memberId];
    if (isEmpty(upsertRequest)) {
      // biome-ignore lint/suspicious/noConsole: unexpected empty upsert path
      console.error('Upsert request is empty', {
        function: 'onRemoveCollectedCards',
        context: 'upsert_request_empty',
        selectedCollectedData,
        currentMember
      });
      return;
    }

    // 선택된 카드들의 collectedCount를 0으로 변경
    const updatedRequest = {};
    Object.keys(upsertRequest).forEach((photoCardId) => {
      updatedRequest[photoCardId] = {
        ...upsertRequest[photoCardId],
        collectedCount: 0
      };
    });

    await handleUpsertCollectedPhotoCards(updatedRequest);
  };

  return (
    <div className="h-fit min-h-screen bg-background-light dark:bg-background-dark pt-[calc(env(safe-area-inset-top))] pb-[calc(env(safe-area-inset-bottom))]">
      <Helmet>
        <title>{t('tabs.collections')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <EditCollectedPhotoCardsHeader
        setIsShowFilterBottomDrawer={setIsShowFilterBottomDrawer}
        onHistoryBackClick={onHistoryBackClick}
        isCheckedPhotoCardFilter={isCheckedPhotoCardFilter()}
      />
      <div className="mt-[10px] mx-[24px] mb-[12px]">
        <SwitchColorModeBar
          isColorModeOnEditable={isColorModeOnEditable}
          onColorModeSwitchChanged={onColorModeSwitchChanged}
        />
      </div>
      <div className="pb-[16px] px-[25px] mb-[96px]">
        <LikedMembersList
          artistMembers={artist?.members?.filter((member) => member.isLiked)}
          onChangeLikedMember={onChangedMember}
        />
        {isUndefined(paginatedEvents)
          ? <EmptyPhotoCardEventSkeletonV2 variant="editorGrid" />
          : paginatedEvents.every((event) => isEmpty(event.photoCards)) && !hasNextPage
            ? <div style={{ width: '100%' }}>
                <EmptyCase emoji={'🥺'} description={t('photoCardPage.emptyPhotoCards')} />
              </div>
            : <div className="w-full">
                <NewPhotoCardsByEventEditor
                  events={paginatedEvents}
                  onClickedPhotoCard={(photoCard, e) => onClickPhotoCard(e, photoCard)}
                  endReached={loadMore}
                  enableAll={false}
                  isColorMode={isColorModeOnEditable}
                  selectedCollectedData={selectedCollectedData}
                  currentMember={currentMember}
                />
                {isFetching && (
                  <Center marginTop={'8px'}>
                    <Spinner
                      size="sm"
                      className="text-primary-light dark:text-primary-dark"
                      marginRight={'20px'}
                    />
                  </Center>
                )}
              </div>}
        {artist && currentMember && (
          <div className="mt-[18px]">
            <ReportPhotoCardBox artist={artist} member={currentMember} />
          </div>
        )}
      </div>
      <CollectedPhotoCardActionsBar
        selectedCollectedData={selectedCollectedData}
        currentMember={currentMember}
        paginatedEvents={paginatedEvents}
        data={data}
        onCompletedEditable={onCompletedEditable}
        onRemoveCollectedCards={onRemoveCollectedCards}
      />
      {isOpenExitDialog && (
        <CommonDialog
          isOpen={isOpenExitDialog}
          onClose={() => setIsOpenExitDialog(false)}
          description={t('photoCardPage.discardAlertDialog')}
          noButtonText={t('no')}
          yesButtonText={t('yes')}
          onClickDialogButton={() => IgnoreAndExit()}
        />
      )}
      {isShowFilterBottomDrawer && (
        <PhotoCardFilterBottomDrawer
          artistId={artistId}
          isOpen={isShowFilterBottomDrawer}
          photoCardCategories={selectedPhotoCardCategories}
          eventIds={selectedEventIds}
          onClose={() => setIsShowFilterBottomDrawer(false)}
          onConfirm={(obj) => onConfirmPhotoCardFilter(obj)}
        />
      )}
    </div>
  );
}
